<template>
  <section :class="isDialog ? 'spm-dialog-root' : 'dp-layout'">
    <section v-if="!isDialog" class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick" />
    </section>

    <section :class="isDialog ? 'spm-dialog-main' : 'dp-right'">
      <div v-if="isDialog" class="spm-dialog-head">
        <div class="spm-dialog-head__info">
          当前学生：<strong>{{ stuName || '-' }}</strong>
        </div>
        <div v-if="dialogHasActions" class="spm-dialog-head__actions">
          <el-button v-if="canBtn('handleAdd')" type="primary" size="small" icon="el-icon-plus" @click="handleAdd">新增监护人</el-button>
          <el-button v-if="canBtn('handleEdit')" type="info" size="small" icon="el-icon-edit" :disabled="!canEditOne" @click="handleEdit">编辑</el-button>
          <el-button v-if="canBtn('handleClearThirdParty')" type="danger" size="small" icon="el-icon-refresh-left" :disabled="!canDelete" @click="handleClearThirdParty">清空第三方账号</el-button>
          <el-button v-if="canBtn('handleDel')" type="danger" size="small" icon="el-icon-delete" :disabled="!canDelete" @click="handleDel">批量删除</el-button>
        </div>
      </div>

      <toolbar v-if="!isDialog" :buttonList="buttonList" @callFunction="callFunction" :searchList="searchList" />

      <div v-if="!isDialog && !departmentId" class="comm-empty-tip">请选择学校/组织后查看家长列表</div>

      <template v-else>
        <!-- 列表：弹窗固定高度时表格吃满剩余空间；页面模式用 tbl-scroll-host。最后更新：2026-10-20 -->
        <div :class="isDialog ? 'spm-dialog-table-host' : 'tbl-scroll-host'">
          <el-table
            :data="parents"
            v-loading="listLoading"
            border
            size="small"
            class="custom-tbl"
            height="100%"
            style="width: 100%"
            row-key="Id"
            @selection-change="handleSelectionChange"
            @row-click="handleRowClick"
            ref="table"
          >
            <el-table-column type="selection" width="48" />
            <el-table-column v-if="!isDialog" prop="DepartmentName" label="所属组织" min-width="130" show-overflow-tooltip />
            <!-- 弹窗顶部已展示当前学生，无需每行再重复姓名 -->
            <el-table-column v-if="!isDialog" label="学生姓名" min-width="100" show-overflow-tooltip>
              <template slot-scope="scope">{{ scope.row._stuName }}</template>
            </el-table-column>
            <el-table-column prop="ParentRelationDesc" label="称谓" width="88" align="center" />
            <el-table-column prop="ParentName" label="监护人姓名" min-width="110" show-overflow-tooltip />
            <el-table-column label="手机/卡号" min-width="130" show-overflow-tooltip>
              <template slot-scope="scope">
                <div>{{ scope.row.ParentMsisdn || '-' }}</div>
                <div v-if="scope.row.CardNumber" class="spm-card-sub">卡：{{ scope.row.CardNumber }}</div>
              </template>
            </el-table-column>
            <el-table-column label="微信绑定" width="100" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row._wxBound ? 'success' : 'info'" size="mini">
                  {{ scope.row._wxBound ? '已绑定' : '未绑定' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="话机分组" width="130" align="center">
              <template slot-scope="scope">
                <el-tag
                  :type="scope.row._voipCount > 0 ? 'success' : 'info'"
                  size="mini"
                  class="voip-auth-tag-clickable"
                  @click.stop="openVoipAuth(scope.row)"
                >
                  <i class="el-icon-phone-outline spm-voip-icon" />
                  {{ scope.row._voipText }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column v-if="showRowActions" label="操作" width="150" align="center">
              <template slot-scope="scope">
                <el-button v-if="canBtn('handleEdit')" type="text" size="small" class="op-edit" @click.stop="handleEditRow(scope.row)">编辑</el-button>
                <el-button v-if="canBtn('handleDel')" type="text" size="small" class="op-delete spm-del-link" @click.stop="handleDelRow(scope.row)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div :class="isDialog ? 'spm-pager' : 'comm-pager'">
          <el-pagination
            layout="total, sizes, prev, pager, next, jumper"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pageSize"
            :total="total"
            :current-page.sync="page"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </template>

      <el-dialog :title="formTitle" :visible.sync="formVisible" width="520px" append-to-body :close-on-click-modal="false">
        <el-form :model="formData" label-width="100px" :rules="formRules" ref="ruleForm">
          <el-form-item v-if="!isDialog && formData.Id <= 0" label="学生" prop="stuId">
            <el-input v-model="formData.stuIdInput" placeholder="请输入学生 ID" />
          </el-form-item>
          <el-form-item v-else label="学生">
            <el-input :value="displayStudentLabel" disabled />
          </el-form-item>
          <el-form-item label="称谓" prop="ParentRelationDesc">
            <div class="relation-tags">
              <button
                v-for="r in relationOptions"
                :key="r"
                type="button"
                class="relation-tag"
                :class="{ active: !relationCustomMode && formData.ParentRelationDesc === r }"
                @click="selectRelation(r)"
              >{{ r }}</button>
              <button
                type="button"
                class="relation-tag"
                :class="{ active: relationCustomMode }"
                @click="enableCustomRelation"
              >自定义</button>
            </div>
            <el-input
              v-if="relationCustomMode"
              ref="customRelationInput"
              v-model="formData.ParentRelationDesc"
              class="relation-custom-input"
              maxlength="20"
              show-word-limit
              placeholder="请输入自定义称谓，如：叔叔、阿姨"
              @input="onCustomRelationInput"
            />
          </el-form-item>
          <el-form-item label="监护人姓名" prop="ParentName">
            <el-input v-model="formData.ParentName" />
          </el-form-item>
          <el-form-item label="电话" prop="ParentMsisdn">
            <el-input v-model="formData.ParentMsisdn" />
          </el-form-item>
          <el-form-item label="车牌">
            <div
              class="vehicle-list"
              :class="{ 'is-scrollable': (formData.vehicleList || []).length > 3 }"
            >
              <div v-for="(v, vIdx) in formData.vehicleList" :key="'vh_' + vIdx" class="vehicle-row">
                <plate-no-input v-model="v.plateNo" style="flex:1;" />
                <el-input v-model="v.vehicleType" placeholder="车型(可选)" style="flex:1;" />
                <el-button v-if="formData.vehicleList.length > 1" type="danger" icon="el-icon-delete" circle size="mini" @click="removeVehicleRow(vIdx)" />
              </div>
            </div>
            <div class="vehicle-list-actions">
              <el-button type="text" icon="el-icon-plus" @click="addVehicleRow">添加车牌</el-button>
              <span class="vehicle-list-count">共 {{ (formData.vehicleList || []).length }} 个</span>
            </div>
          </el-form-item>
          <el-form-item label="卡号">
            <el-input v-model="formData.CardNumber" />
          </el-form-item>
          <el-alert
            class="spm-form-alert"
            type="info"
            :closable="false"
            show-icon
          >
            <template slot="title">
              <span class="spm-form-alert__text">第三方账号一般为钉钉 userid，可手动填写绑定；微信 OpenId 由家长端公众号绑定自动生成。批量「清空第三方账号」<strong>不会</strong>清除微信 OpenId。</span>
            </template>
          </el-alert>
          <el-form-item label="第三方账号">
            <el-input
              v-model="formData.ThirdPartyCode"
              clearable
              placeholder="钉钉 userid，可手动填写"
            />
          </el-form-item>
          <el-form-item label="微信 OpenId">
            <div class="spm-bind-row">
              <el-input :value="formData.ParentWxOpenId || ''" disabled placeholder="家长端微信绑定后自动写入" />
              <el-tag size="mini" :type="formData.ParentWxOpenId ? 'success' : 'info'" effect="plain">
                {{ formData.ParentWxOpenId ? '已绑定' : '未绑定' }}
              </el-tag>
            </div>
          </el-form-item>
          <el-form-item v-if="formData.ParentWxAppId" label="公众号 AppId">
            <el-input :value="formData.ParentWxAppId" disabled />
          </el-form-item>
        </el-form>
        <div slot="footer">
          <el-button @click="formVisible = false">取消</el-button>
          <el-button type="primary" :loading="formLoading" @click="handleSubmit">提交</el-button>
        </div>
      </el-dialog>

      <parent-voip-auth-dialog
        :visible.sync="voipDialog.visible"
        :parent-id="voipDialog.parentId"
        :department-id="voipDialog.departmentId"
        :parent-name="voipDialog.parentName"
        :student-name="voipDialog.studentName"
        :relation-desc="voipDialog.relationDesc"
        :wx-bound="voipDialog.wxBound"
        :voip-auth-count="voipDialog.voipAuthCount"
      />

      <!-- 监护人姓名错乱检测（疑似旧版钉钉同步挂错）。创建于 2026-09-03 -->
      <el-dialog 
        title="监护人姓名错乱检测"
        :visible.sync="mismatchDialog.visible"
        width="960px"
        append-to-body
        :close-on-click-modal="false"
      >
        <div class="spm-mismatch-tip">
          检测规则：监护人姓名以「家长/爸爸/妈妈…」等称谓结尾，但未以当前关联学生姓名开头。仅供人工核对，不会自动改数。
        </div>
        <div class="spm-mismatch-toolbar">
          <el-input
            v-model="mismatchDialog.keyword"
            clearable
            size="small"
            placeholder="搜索学生 / 监护人 / 第三方账号 / 组织 / ID"
            prefix-icon="el-icon-search"
            class="spm-mismatch-search"
            @keyup.enter.native="handleMismatchSearch"
            @clear="handleMismatchClear"
          />
          <el-button type="primary" size="small" icon="el-icon-search" :loading="mismatchDialog.loading" @click="handleMismatchSearch">查询</el-button>
          <div class="spm-mismatch-summary">
            <el-tag type="info" size="small">已扫描：{{ mismatchDialog.scannedCount }}</el-tag>
            <el-tag :type="mismatchDialog.mismatchCount > 0 ? 'danger' : 'success'" size="small">
              疑似错乱：{{ mismatchDialog.mismatchCount }}
            </el-tag>
          </div>
        </div>
        <el-empty
          v-if="!mismatchDialog.loading && !(mismatchDialog.items || []).length"
          description="未发现姓名错乱"
        />
        <el-table
          v-else
          :data="mismatchDialog.items"
          v-loading="mismatchDialog.loading"
          border
          size="small"
          max-height="420"
          class="custom-tbl"
        >
          <el-table-column prop="departmentName" label="所属组织" min-width="130" show-overflow-tooltip />
          <el-table-column prop="stuName" label="关联学生" min-width="100" show-overflow-tooltip />
          <el-table-column prop="parentName" label="监护人姓名" min-width="120" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="spm-mismatch-name">{{ scope.row.parentName }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="parentRelationDesc" label="称谓" width="80" align="center" />
          <el-table-column prop="thirdPartyCode" label="第三方账号" min-width="130" show-overflow-tooltip />
          <el-table-column prop="reason" label="说明" min-width="160" show-overflow-tooltip />
          <el-table-column label="操作" width="140" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" size="mini" class="op-edit" @click="editMismatchRow(scope.row)">编辑</el-button>
              <el-button type="text" size="mini" class="op-delete spm-del-link" @click="removeMismatchRow(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div v-if="mismatchDialog.mismatchCount > 0" class="spm-mismatch-pager">
          <el-pagination
            local-paging
            layout="total, sizes, prev, pager, next, jumper"
            :page-sizes="[5, 10, 20, 50]"
            :page-size="mismatchDialog.pageSize"
            :total="mismatchDialog.mismatchCount"
            :current-page.sync="mismatchDialog.page"
            @size-change="handleMismatchSizeChange"
            @current-change="handleMismatchPageChange"
          />
        </div>
        <span slot="footer" class="dialog-footer">
          <el-button @click="mismatchDialog.visible = false">关 闭</el-button>
          <el-button type="primary" :loading="mismatchDialog.loading" @click="handleDetectNameMismatch">刷 新</el-button>
        </span>
      </el-dialog>
    </section>
  </section>
</template>

<script>
import '../../Communication/styles/comm-page.css'
import DepartmentPane from '../../../components/DepartmentPane'
import PlateNoInput from '@/components/PlateNoInput'
import ParentVoipAuthDialog from './ParentVoipAuthDialog.vue'
import Toolbar from '../../../components/Toolbar'
import { getButtonList } from '../../../promissionRouter'
import {
  getStudentParentManageList,
  detectStudentParentNameMismatch,
  addStudentParent,
  editStudentParent,
  removeStudentParent,
  batchDeleteStudentParent,
  batchClearStudentParentThirdParty,
  saveStudentParentVehicles
} from '../../../api/modules/personnel'

/** 家长管理按钮权限固定路由（页面与 Person 弹窗共用）。最后更新：2026-08-30 */
const PARENT_MANAGE_BUTTON_ROUTE = '/User/ParentManage'

export default {
  name: 'StudentParentManagement',
  components: { DepartmentPane, ParentVoipAuthDialog, Toolbar, PlateNoInput },
  props: {
    isDialog: { type: Boolean, default: false },
    stuId: { type: [Number, String], default: 0 },
    stuName: { type: String, default: '' }
  },
  data() {
    return {
      departmentId: 0,
      parents: [],
      total: 0,
      page: 1,
      pageSize: 10,
      listLoading: false,
      multipleSelection: [],
      filters: {},
      searchList: [
        { label: '学生姓名', key: 'stuName', placeholder: '学生姓名' },
        { label: '监护人姓名', key: 'parentName', placeholder: '姓名' },
        { label: '手机/卡号', key: 'msisdnCard', placeholder: '手机或卡号' },
        {
          label: '微信绑定',
          key: 'isWxBind',
          type: 'select',
          options: [
            { label: '全部', value: '' },
            { label: '已绑定', value: '1' },
            { label: '未绑定', value: '0' }
          ]
        },
        {
          label: '话机订阅',
          key: 'isVoipAuth',
          type: 'select',
          options: [
            { label: '全部', value: '' },
            { label: '已订阅', value: '1' },
            { label: '未订阅', value: '0' }
          ]
        }
      ],
      buttonList: [],
      formVisible: false,
      formTitle: '新增监护人',
      formLoading: false,
      formData: this.createEmptyForm(),
      relationOptions: ['爸爸', '妈妈', '爷爷', '奶奶', '外公', '外婆', '哥哥', '姐姐', '其他'],
      /** 是否正在使用自定义称谓输入 */
      relationCustomMode: false,
      formRules: {
        ParentName: [{ required: true, message: '请输入监护人姓名', trigger: 'blur' }],
        ParentRelationDesc: [{ required: true, message: '请选择或填写称谓', trigger: ['blur', 'change'] }]
      },
      voipDialog: {
        visible: false,
        parentId: 0,
        departmentId: 0,
        parentName: '',
        studentName: '',
        relationDesc: '',
        wxBound: false,
        voipAuthCount: 0
      },
      /** 姓名错乱检测弹窗。创建于 2026-09-03 */
      mismatchDialog: {
        visible: false,
        loading: false,
        scannedCount: 0,
        mismatchCount: 0,
        keyword: '',
        page: 1,
        pageSize: 10,
        requestSeq: 0,
        items: []
      }
    }
  },
  computed: {
    canEditOne() {
      return this.multipleSelection && this.multipleSelection.length === 1
    },
    canDelete() {
      return this.multipleSelection && this.multipleSelection.length > 0
    },
    displayStudentLabel() {
      const name = this.formData.StuName || this.stuName || ''
      const id = this.formData.StuId || this.stuId || ''
      return name ? `${name} (${id})` : String(id || '-')
    },
    showRowActions() {
      return this.canBtn('handleEdit') || this.canBtn('handleDel')
    },
    dialogHasActions() {
      return this.canBtn('handleAdd') || this.canBtn('handleEdit') || this.canBtn('handleDel') || this.canBtn('handleClearThirdParty')
    }
  },
  watch: {
    stuId(val) {
      if (this.isDialog && val) {
        this.page = 1
        this.handleQuery()
      }
    }
  },
  mounted() {
    const routers = window.localStorage.router ? JSON.parse(window.localStorage.router) : []
    this.buttonList = getButtonList(PARENT_MANAGE_BUTTON_ROUTE, routers)
    // 本地注入检测按钮（无需等菜单权限 SQL）。创建于 2026-09-03
    if (!this.isDialog && !this.buttonList.some((item) => item.Func === 'handleDetectNameMismatch')) {
      this.buttonList.push({
        name: '姓名错乱检测',
        Func: 'handleDetectNameMismatch',
        id: 997,
        IsHide: false,
        btnType: 'warning',
        icon: 'el-icon-warning-outline'
      })
    }
    this.updateButtonStatus(this.multipleSelection)
    if (this.isDialog && this.stuId) {
      this.handleQuery()
    }
  },
  methods: {
    /** 是否拥有指定按钮权限；未配置 buttonList 时默认放行（兼容 SQL 未执行前） */
    canBtn(func) {
      if (!this.buttonList || !this.buttonList.length) return true
      // 本地注入的检测按钮不参与权限闸门判断，避免空权限列表被「撑开」后其它按钮全部隐藏
      const permButtons = this.buttonList.filter((btn) => btn.Func !== 'handleDetectNameMismatch')
      if (!permButtons.length) return true
      return permButtons.some((btn) => btn.Func === func)
    },
    /** 根据选中行更新编辑/删除/清空按钮禁用状态 */
    updateButtonStatus(sels) {
      const needSelectFuncs = ['handleEdit', 'handleDel', 'handleClearThirdParty']
      this.buttonList.forEach((btn) => {
        if (!needSelectFuncs.includes(btn.Func)) return
        if (btn.Func === 'handleEdit') {
          this.$set(btn, 'Disabled', !sels || sels.length !== 1)
        } else {
          this.$set(btn, 'Disabled', !sels || sels.length === 0)
        }
      })
    },
    callFunction(item) {
      if (typeof item.search === 'object') {
        this.filters = item.search || {}
      } else {
        this.filters = { key: item.search }
      }
      this[item.Func].apply(this, item)
    },
    /** 点选预设称谓 */
    selectRelation(relation) {
      this.relationCustomMode = false
      this.formData.ParentRelationDesc = relation || ''
      this.$nextTick(() => {
        if (this.$refs.ruleForm) this.$refs.ruleForm.validateField('ParentRelationDesc')
      })
    },
    /** 切换到自定义称谓输入 */
    enableCustomRelation() {
      this.relationCustomMode = true
      if (this.relationOptions.indexOf(this.formData.ParentRelationDesc) !== -1) {
        this.formData.ParentRelationDesc = ''
      }
      this.$nextTick(() => {
        const input = this.$refs.customRelationInput
        if (input && input.focus) input.focus()
        if (this.$refs.ruleForm) this.$refs.ruleForm.clearValidate('ParentRelationDesc')
      })
    },
    onCustomRelationInput() {
      if (this.$refs.ruleForm) this.$refs.ruleForm.validateField('ParentRelationDesc')
    },
    /** 按当前称谓值同步自定义模式（编辑历史数据时用） */
    syncRelationCustomMode() {
      const val = (this.formData && this.formData.ParentRelationDesc) || ''
      this.relationCustomMode = !!(val && this.relationOptions.indexOf(val) === -1)
    },
    createEmptyForm() {
      return {
        Id: 0,
        StuId: '',
        StuName: '',
        stuIdInput: '',
        ParentName: '',
        ParentRelationDesc: '',
        ParentMsisdn: '',
        Account: '',
        CardNumber: '',
        ThirdPartyCode: '',
        ParentWxOpenId: '',
        ParentWxAppId: '',
        ParentOrderNo: 0,
        vehicleList: [{ plateNo: '', vehicleType: '' }]
      }
    },
    treeNodeClick(data) {
      this.departmentId = data && data.value ? data.value : 0
      this.page = 1
      this.handleQuery()
    },
    handleQuery() {
      this.page = 1
      this.loadList()
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.page = 1
      this.loadList()
    },
    handleCurrentChange(val) {
      this.page = val
      this.loadList()
    },
    handleSelectionChange(rows) {
      this.multipleSelection = rows || []
      this.updateButtonStatus(this.multipleSelection)
    },
    handleRowClick(row) {
      if (!row || !this.$refs.table) return
      // 点击行切换选中，支持多选（与 Person.vue 一致）
      this.$refs.table.toggleRowSelection(row)
    },
    selectedIds() {
      return (this.multipleSelection || [])
        .map((row) => Number(row.Id != null ? row.Id : row.id))
        .filter((id) => id > 0)
    },
    isWxBound(row) {
      return !!(row && (row.ParentWxOpenId || row.parentWxOpenId))
    },
    voipAuthCount(row) {
      const n = row && (row.VoipAuthCount != null ? row.VoipAuthCount : row.voipAuthCount)
      return Number(n) || 0
    },
    /** 列表行展示字段预计算，减轻模板内重复方法调用。最后更新：2026-08-31 */
    decorateParentRows(rows) {
      return (rows || []).map((row) => {
        const wxBound = this.isWxBound(row)
        const voipCount = this.voipAuthCount(row)
        let voipText = '未订阅'
        if (wxBound) {
          voipText = voipCount > 0 ? `已订阅 (${voipCount})` : '未订阅 (0)'
        }
        return Object.assign(row, {
          _stuName: row.StuName || row.stuName || '-',
          _wxBound: wxBound,
          _voipCount: voipCount,
          _voipText: voipText
        })
      })
    },
    resolveVoipDepartmentId(row) {
      return Number(
        row.StudentDepartmentId || row.studentDepartmentId
        || this.departmentId
        || 0
      )
    },
    openVoipAuth(row) {
      this.voipDialog = {
        visible: true,
        parentId: row.Id != null ? row.Id : row.id,
        departmentId: this.resolveVoipDepartmentId(row),
        parentName: row.ParentName || row.parentName || '',
        studentName: row.StuName || row.stuName || this.stuName || '',
        relationDesc: row.ParentRelationDesc || row.parentRelationDesc || '',
        wxBound: !!row._wxBound || this.isWxBound(row),
        voipAuthCount: row._voipCount != null ? row._voipCount : this.voipAuthCount(row)
      }
    },
    async loadList() {
      if (this.isDialog) {
        if (!this.stuId) {
          this.parents = []
          this.total = 0
          return
        }
      } else if (!this.departmentId) {
        this.parents = []
        this.total = 0
        return
      }

      const para = {
        page: this.page,
        pageSize: this.pageSize,
        stuName: (this.filters.stuName || '').trim() || undefined,
        parentName: (this.filters.parentName || '').trim() || undefined,
        msisdnCard: (this.filters.msisdnCard || '').trim() || undefined,
        isWxBind: this.filters.isWxBind || undefined,
        isVoipAuth: this.filters.isVoipAuth || undefined
      }
      if (this.isDialog) {
        para.stuUid = this.stuId
      } else {
        para.departmentId = this.departmentId
      }

      this.listLoading = true
      try {
        const res = await getStudentParentManageList(para)
        if (res.data.success) {
          const pageData = res.data.response || {}
          this.parents = this.decorateParentRows(pageData.data || [])
          this.total = pageData.dataCount || 0
        } else {
          this.$message.error(res.data.msg || '加载失败')
        }
      } finally {
        this.listLoading = false
      }
    },
    handleAdd() {
      this.formTitle = '新增监护人'
      this.formData = this.createEmptyForm()
      this.relationCustomMode = false
      if (this.isDialog) {
        this.formData.StuId = this.stuId
        this.formData.StuName = this.stuName
      }
      this.formVisible = true
      this.$nextTick(() => {
        if (this.$refs.ruleForm) this.$refs.ruleForm.clearValidate()
      })
    },
    handleEdit() {
      if (!this.canEditOne) return
      this.handleEditRow(this.multipleSelection[0])
    },
    handleEditRow(row) {
      this.formTitle = '编辑监护人'
      this.formData = Object.assign(this.createEmptyForm(), row, {
        vehicleList: [{ plateNo: '', vehicleType: '' }]
      })
      const vehItems = row.VehicleItems || row.vehicleItems || []
      if (vehItems.length) {
        this.formData.vehicleList = vehItems.map((it) => ({
          plateNo: it.plateNo || it.PlateNo || '',
          vehicleType: it.vehicleType || it.VehicleType || ''
        }))
      }
      this.syncRelationCustomMode()
      this.formVisible = true
      this.$nextTick(() => {
        if (this.$refs.ruleForm) this.$refs.ruleForm.clearValidate()
      })
    },
    /** 批量删除选中监护人。最后更新：2026-08-31 */
    handleDel() {
      const ids = this.selectedIds()
      if (!ids.length) {
        this.$message.warning('请先勾选要删除的监护人')
        return
      }
      const tip = ids.length === 1
        ? `确认删除监护人【${this.multipleSelection[0].ParentName || ''}】吗？`
        : `确认批量删除选中的 ${ids.length} 条监护人吗？`
      this.$confirm(tip, '提示', { type: 'warning' }).then(async () => {
        const res = ids.length === 1
          ? await removeStudentParent({ id: ids[0] })
          : await batchDeleteStudentParent(ids)
        if (res.data.success) {
          this.$message.success(res.data.msg || '删除成功')
          this.handleQuery()
        } else {
          this.$message.error(res.data.msg || '删除失败')
        }
      }).catch(() => {})
    },
    handleDelRow(row) {
      if (!row || !row.Id) return
      this.$confirm(`确认删除监护人【${row.ParentName || ''}】吗？`, '提示', { type: 'warning' }).then(async () => {
        const res = await removeStudentParent({ id: row.Id })
        if (res.data.success) {
          this.$message.success('删除成功')
          this.handleQuery()
        } else {
          this.$message.error(res.data.msg || '删除失败')
        }
      }).catch(() => {})
    },
    /**
     * 检测当前组织下监护人姓名与学生不一致的记录。创建于 2026-09-03
     */
    async handleDetectNameMismatch(options = {}) {
      if (this.isDialog) return
      if (!this.departmentId) {
        this.$message.warning('请先在左侧选择学校/组织后再检测')
        return
      }
      // Toolbar 可能把按钮对象当成参数传入
      const opt = (options && typeof options === 'object' && !Array.isArray(options) && options.Func == null)
        ? options
        : {}

      const opening = !this.mismatchDialog.visible
      if (opening || opt.resetFilter) {
        this.mismatchDialog.keyword = ''
        this.mismatchDialog.page = 1
      } else if (opt.resetPage) {
        this.mismatchDialog.page = 1
      }

      this.mismatchDialog.visible = true
      this.mismatchDialog.loading = true
      const seq = ++this.mismatchDialog.requestSeq
      const keyword = (this.mismatchDialog.keyword || '').trim()
      try {
        const res = await detectStudentParentNameMismatch({
          departmentId: this.departmentId,
          keyword: keyword || undefined,
          page: this.mismatchDialog.page,
          intPageSize: this.mismatchDialog.pageSize
        })
        if (seq !== this.mismatchDialog.requestSeq) return
        if (res.data.success) {
          const data = res.data.response || {}
          this.mismatchDialog.scannedCount = data.scannedCount || 0
          this.mismatchDialog.mismatchCount = data.mismatchCount || 0
          this.mismatchDialog.items = data.items || []
          if (data.page) this.mismatchDialog.page = data.page
          if (!(data.mismatchCount > 0)) {
            this.$message.success(res.data.msg || '未发现姓名错乱')
          } else if (!opt.silentMsg) {
            this.$message.warning(res.data.msg || `发现 ${data.mismatchCount} 条疑似错乱`)
          }
        } else {
          this.$message.error(res.data.msg || '检测失败')
        }
      } catch (e) {
        if (seq !== this.mismatchDialog.requestSeq) return
        this.$message.error('检测失败，请稍后重试')
      } finally {
        if (seq === this.mismatchDialog.requestSeq) {
          this.mismatchDialog.loading = false
        }
      }
    },
    /** 关键字查询（回到第 1 页）。创建于 2026-09-03 */
    handleMismatchSearch() {
      this.handleDetectNameMismatch({ resetPage: true, silentMsg: true })
    },
    /** 清空关键字后重查。创建于 2026-09-03 */
    handleMismatchClear() {
      this.mismatchDialog.keyword = ''
      this.$nextTick(() => {
        this.handleDetectNameMismatch({ resetPage: true, silentMsg: true })
      })
    },
    handleMismatchSizeChange(val) {
      this.mismatchDialog.pageSize = val
      this.mismatchDialog.page = 1
      this.handleDetectNameMismatch({ silentMsg: true })
    },
    handleMismatchPageChange(val) {
      this.mismatchDialog.page = val
      this.handleDetectNameMismatch({ silentMsg: true })
    },
    /** 从错乱列表打开编辑。创建于 2026-09-03 */
    editMismatchRow(row) {
      if (!row || !row.parentId) return
      this.handleEditRow({
        Id: row.parentId,
        StuId: row.stuId,
        StuName: row.stuName,
        ParentName: row.parentName,
        ParentRelationDesc: row.parentRelationDesc,
        ThirdPartyCode: row.thirdPartyCode,
        ParentMsisdn: '',
        CardNumber: '',
        ParentWxOpenId: '',
        ParentWxAppId: '',
        ParentOrderNo: 0,
        VehicleItems: []
      })
    },
    /** 从错乱列表删除监护人并刷新检测结果。创建于 2026-09-03 */
    removeMismatchRow(row) {
      if (!row || !row.parentId) return
      this.$confirm(
        `确认删除监护人【${row.parentName || ''}】（关联学生：${row.stuName || '-'}）吗？`,
        '提示',
        { type: 'warning' }
      ).then(async () => {
        const res = await removeStudentParent({ id: row.parentId })
        if (res.data.success) {
          this.$message.success('删除成功')
          this.handleQuery()
          this.handleDetectNameMismatch()
        } else {
          this.$message.error(res.data.msg || '删除失败')
        }
      }).catch(() => {})
    },
    /** 批量清空选中监护人的第三方账号。最后更新：2026-08-31 */
    handleClearThirdParty() {
      const ids = this.selectedIds()
      if (!ids.length) {
        this.$message.warning('请先勾选要清空第三方账号的监护人')
        return
      }
      this.$confirm(
        `确认清空选中的 ${ids.length} 条监护人的第三方账号吗？\n仅清除钉钉/厂家等第三方标识，不会清空微信 OpenId。`,
        '清空第三方账号',
        { type: 'warning' }
      ).then(async () => {
        const res = await batchClearStudentParentThirdParty(ids)
        if (res.data.success) {
          this.$message.success(res.data.msg || '清空成功')
          this.handleQuery()
        } else {
          this.$message.error(res.data.msg || '清空失败')
        }
      }).catch(() => {})
    },
    addVehicleRow() {
      this.formData.vehicleList.push({ plateNo: '', vehicleType: '' })
    },
    removeVehicleRow(idx) {
      if (this.formData.vehicleList.length <= 1) return
      this.formData.vehicleList.splice(idx, 1)
    },
    buildVehiclePayload() {
      return (this.formData.vehicleList || [])
        .map((v) => ({
          plateNo: (v.plateNo || '').trim(),
          vehicleType: (v.vehicleType || '').trim()
        }))
        .filter((v) => v.plateNo)
    },
    handleSubmit() {
      this.$refs.ruleForm.validate((valid) => {
        if (!valid) return
        this.$confirm('确认提交吗？', '提示', {}).then(async () => {
          this.formLoading = true
          try {
            let stuId = this.formData.StuId
            if (!this.isDialog && this.formData.Id <= 0) {
              stuId = this.formData.stuIdInput
            }
            if (!stuId) {
              this.$message.warning('请填写学生 ID')
              return
            }

            const parentPayload = {
              Id: this.formData.Id,
              StuId: stuId,
              ParentName: this.formData.ParentName,
              ParentRelationDesc: this.formData.ParentRelationDesc,
              ParentMsisdn: this.formData.ParentMsisdn,
              Account: this.formData.Account,
              CardNumber: this.formData.CardNumber,
              ThirdPartyCode: (this.formData.ThirdPartyCode || '').trim(),
              ParentOrderNo: this.formData.ParentOrderNo
            }
            if (this.isDialog) {
              parentPayload.UserId = stuId
            }

            const api = parentPayload.Id > 0 ? editStudentParent : addStudentParent
            const res = await api(parentPayload)
            if (!res.data.success) {
              this.$message.error(res.data.msg || '保存失败')
              return
            }

            let parentId = parentPayload.Id
            if (parentPayload.Id <= 0) {
              parentId = res.data.response
            }

            const items = this.buildVehiclePayload()
            if (parentId && stuId) {
              const vRes = await saveStudentParentVehicles({
                parentId: Number(parentId),
                stuId: Number(stuId),
                items
              })
              if (!vRes.data.success) {
                this.$message.warning(vRes.data.msg || '监护人已保存，但车牌保存失败')
              }
            }

            this.$message.success(res.data.msg || '保存成功')
            this.formVisible = false
            this.handleQuery()
            if (this.mismatchDialog.visible) {
              this.handleDetectNameMismatch()
            }
          } finally {
            this.formLoading = false
          }
        }).catch(() => {})
      })
    }
  }
}
</script>

<style scoped>
/* 弹窗模式：铺满固定高度，表格占剩余空间。最后修改：2026-10-20 */
.spm-dialog-root,
.spm-dialog-main {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.spm-dialog-table-host {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
}
.spm-dialog-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  flex-shrink: 0;
}
.spm-dialog-head__info {
  font-size: 13px;
  color: #475569;
  min-width: 0;
  flex: 1 1 auto;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.spm-dialog-head__info strong {
  color: #6366f1;
  font-size: 14px;
}
.spm-dialog-head__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex: 0 1 auto;
  min-width: 0;
  justify-content: flex-end;
}
.spm-pager {
  display: flex;
  justify-content: flex-end;
  padding: 14px 4px 6px;
  flex-shrink: 0;
}
.spm-card-sub {
  color: #909399;
  font-size: 12px;
}
.spm-voip-icon {
  margin-right: 2px;
}
.spm-form-alert {
  margin-bottom: 12px;
}
.relation-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  padding-top: 2px;
}
.relation-tag {
  margin: 0;
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 16px;
  background: #fff;
  color: #606266;
  font-size: 13px;
  line-height: 1.2;
  cursor: pointer;
  transition: all 0.15s ease;
}
.relation-tag:hover {
  border-color: #409eff;
  color: #409eff;
}
.relation-tag.active {
  border-color: #409eff;
  background: #409eff;
  color: #fff;
}
.relation-custom-input {
  margin-top: 10px;
  width: 100%;
}
.spm-form-alert__text {
  font-size: 12px;
  line-height: 1.5;
  color: #606266;
}
.spm-form-alert__text strong {
  color: #e6a23c;
  font-weight: 600;
}
.spm-bind-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
.spm-bind-row .el-input {
  flex: 1;
}
.spm-del-link,
.spm-del-link.el-button--text {
  color: #f56c6c !important;
}
.spm-del-link:hover,
.spm-del-link.el-button--text:hover {
  color: #f78989 !important;
}
.vehicle-list {
  width: 100%;
}
.vehicle-list.is-scrollable {
  max-height: 144px;
  overflow-y: auto;
  padding-right: 4px;
  margin-bottom: 4px;
}
.vehicle-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}
.vehicle-list .vehicle-row:last-child {
  margin-bottom: 0;
}
.vehicle-list-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 2px;
}
.vehicle-list-count {
  color: #94a3b8;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
}
.voip-auth-tag-clickable {
  cursor: pointer;
}
.spm-mismatch-tip {
  color: #606266;
  font-size: 12px;
  line-height: 1.6;
  margin-bottom: 10px;
}
.spm-mismatch-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.spm-mismatch-search {
  width: 300px;
  max-width: 100%;
}
.spm-mismatch-summary {
  margin-left: auto;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.spm-mismatch-name {
  color: #e6a23c;
  font-weight: 600;
}
.spm-mismatch-pager {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}
</style>
